import {
  type PropsWithChildren,
  type ReactElement,
  useEffect,
  useRef,
} from 'react';
// biome-ignore lint/style/noRestrictedImports: legacy react-modal, moving to UAD Modal in FE-8055
import ReactModal from 'react-modal';

import classNames from 'classnames';
import { uniqueId } from 'lodash-es';

import HelpButton from '../../components/help_button/help_button';
import { ICONS } from '../../icons/icon';
import { fullscreenElementOrBody } from '../../utils/fullscreen_element_or_body';
import { ui } from '../../utils/helpers';
import IconButton from '../icon_button/icon_button';
import { VARIANTS } from '../label/label';

import styles from './modal.module.css';

if (typeof window !== 'undefined') {
  const element =
    document.querySelector<HTMLElement>('#root') ||
    document.createElement('div');
  ReactModal.setAppElement(element);
}

export type ModalVariant =
  | 'danger'
  | 'warning'
  | 'success'
  | 'info'
  | 'neutral';

export type ModalSizes = 's' | 'm' | 'l' | 'xl' | 'xxl';

type Props = {
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  modalAfterOpenClassName?: string;
  modalBeforeCloseClassName?: string;
  overlayClassName?: string;
  overlayAfterOpenClassName?: string;
  overlayBeforeCloseClassName?: string;
  isOpen?: boolean;
  onRequestClose?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  onAfterClose?: () => void;
  onOpen?: () => void;
  headerText?: string | ReactElement;
  variant?: ModalVariant;
  closeButton?: boolean;
  helpPath?: string;
  helpTitle?: string;
  contentClassName?: string;
  size?: ModalSizes;
  shouldCloseOnOverlayClick?: boolean;
};

const Modal = ({
  className,
  headerClassName,
  bodyClassName,
  modalAfterOpenClassName,
  modalBeforeCloseClassName,
  overlayClassName,
  overlayAfterOpenClassName,
  overlayBeforeCloseClassName,
  isOpen = false,
  onRequestClose,
  onAfterClose,
  onOpen,
  headerText,
  closeButton = true,
  helpPath,
  helpTitle,
  children,
  contentClassName,
  size = 'm',
  shouldCloseOnOverlayClick = true,
}: PropsWithChildren<Props>): ReactElement => {
  // Including onOpen in deps will require to properly memoize it in every usage
  // biome-ignore lint/correctness/useExhaustiveDependencies: side effect
  useEffect(() => {
    if (isOpen) {
      onOpen?.();
    }
  }, [isOpen]);

  const labelId = useRef(uniqueId('modal_heading_'));

  return (
    <ReactModal
      aria={{
        labelledby: labelId.current,
      }}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onAfterClose={onAfterClose}
      closeTimeoutMS={200}
      className={{
        base: classNames(styles.modal, className, styles[`size_${size}`]),
        afterOpen: classNames(styles.modalAfterOpen, modalAfterOpenClassName),
        beforeClose: classNames(
          styles.modalBeforeClose,
          modalBeforeCloseClassName,
        ),
      }}
      overlayClassName={{
        base: classNames(styles.overlay, overlayClassName),
        afterOpen: classNames(
          styles.overlayAfterOpen,
          overlayAfterOpenClassName,
        ),
        beforeClose: classNames(
          styles.overlayBeforeClose,
          overlayBeforeCloseClassName,
        ),
      }}
      portalClassName="modal_portal"
      bodyOpenClassName="modal_body_open"
      htmlOpenClassName="modal_html_open"
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      parentSelector={fullscreenElementOrBody}
    >
      <div
        className={classNames(styles.root, contentClassName)}
        {...ui('modal-content')}
      >
        {headerText !== undefined ? (
          <div className={classNames(styles.header, headerClassName)}>
            <div
              id={labelId.current}
              className={styles.title}
              role="heading"
              aria-level={1}
            >
              {headerText}
              {helpPath && (
                <HelpButton
                  path={helpPath}
                  title={helpTitle || ''}
                  className={styles.helpButton}
                  variant={VARIANTS.WHITE}
                />
              )}
            </div>

            {closeButton && (
              <IconButton
                icon={ICONS.CrossMark}
                className={styles.closeButton}
                onClick={onRequestClose}
                aria-label="Close"
              />
            )}
          </div>
        ) : null}
        <div className={classNames(styles.content, bodyClassName)}>
          {children}
        </div>
      </div>
    </ReactModal>
  );
};

export default Modal;
